import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { useAppContextNonNull } from "@contexts/AppContext";
import UnanetService from "@services/UnanetService";
import IOwTableCols from "@models/IOwTableCols";
import { IUnanetCategoryMapping, IUnanetAccountMappingsSummary } from "@models/Unanet/IUnanetAccountMappingsResponse";
import { UnanetGlAccountMappingEdit } from "./UnanetGlAccountMappingEdit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMatch } from "react-router-dom";
import "./UnanetGlAccountMapping.scss";

type FilterMode = "all" | "configured" | "inherited" | "unconfigured";

export const UnanetGlAccountMapping = () => {
    const match = useMatch("catalogs/:guid/unanet/gl-account-mapping");
    const catalogGuid = match?.params?.guid || "";

    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [categories, setCategories] = useState<IUnanetCategoryMapping[]>([]);
    const [summary, setSummary] = useState<IUnanetAccountMappingsSummary | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [filterMode, setFilterMode] = useState<FilterMode>("all");

    const navItems = useMemo(() => {
        return [
            { title: "catalogs", url: "/catalogs" },
            { title: "unanet_gl_account_mapping", url: `/catalogs/${catalogGuid}/unanet/gl-account-mapping` },
        ];
    }, [catalogGuid]);

    const fetchMappings = useCallback(async () => {
        if (!catalogGuid) return;
        setIsLoading(true);
        try {
            const data = await UnanetService.getAccountMappings(catalogGuid, userModel, supplierModel);
            setCategories(data.categories || []);
            setSummary(data.summary || null);
        } catch (e) {
            console.error("Error fetching account mappings:", e);
        } finally {
            setIsLoading(false);
            setIsInitialLoad(false);
        }
    }, [catalogGuid, userModel, supplierModel]);

    useEffect(() => {
        document.title = `Orderwerks >> Catalogs >> Unanet GL Account Mapping`;
        fetchMappings();
    }, [fetchMappings]);

    const filteredCategories = useMemo(() => {
        if (filterMode === "all") return categories;
        return categories.filter((cat) => {
            if (filterMode === "configured") return cat.hasDirectMapping;
            if (filterMode === "inherited") return !cat.hasDirectMapping && cat.inheritedFrom != null;
            if (filterMode === "unconfigured") return !cat.hasDirectMapping && cat.inheritedFrom == null && cat.effectiveMapping == null;
            return true;
        });
    }, [categories, filterMode]);

    const openEditSidebar = useCallback(
        (category: IUnanetCategoryMapping) => {
            uiContext.setSidebarComponent(
                <UnanetGlAccountMappingEdit
                    category={category}
                    catalogGuid={catalogGuid}
                    onClose={(refresh) => {
                        uiContext.toggleSidebar(false);
                        if (refresh) {
                            fetchMappings();
                        }
                    }}
                />
            );
            uiContext.toggleSidebar(true);
        },
        [uiContext, catalogGuid, fetchMappings]
    );

    const renderAccountCell = useCallback((cat: IUnanetCategoryMapping, accountType: "revenue" | "cogs" | "inventory") => {
        const isInherited = !cat.hasDirectMapping && cat.inheritedFrom != null;
        const mapping = cat.mapping || cat.effectiveMapping;

        let code: string | null | undefined;
        let name: string | null | undefined;
        let hasValue = false;

        if (mapping) {
            if (accountType === "revenue") {
                code = mapping.revenue_account_code;
                name = mapping.revenue_account_name;
                hasValue = mapping.revenue_account_key != null;
            } else if (accountType === "cogs") {
                code = mapping.cogs_account_code;
                name = mapping.cogs_account_name;
                hasValue = mapping.cogs_account_key != null;
            } else {
                code = mapping.inventory_account_code;
                name = mapping.inventory_account_name;
                hasValue = mapping.inventory_account_key != null;
            }
        }

        if (!hasValue) {
            return <span className={"UnanetGlAccountMapping__not-configured"}>[Not Configured]</span>;
        }

        return (
            <div className={`UnanetGlAccountMapping__account-cell ${isInherited ? "UnanetGlAccountMapping__account-inherited" : ""}`}>
                <span className={"UnanetGlAccountMapping__account-code"}>{code}</span>
                {name && <span className={"UnanetGlAccountMapping__account-desc"}>{name}</span>}
            </div>
        );
    }, []);

    const columns: IOwTableCols[] = useMemo(
        () => [
            {
                title: "category",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetCategoryMapping) => {
                    return (
                        <div className={"UnanetGlAccountMapping__category-cell"} style={{ paddingLeft: row.depth * 24 }}>
                            {row.depth > 0 && <span className={"UnanetGlAccountMapping__category-connector"}>{"└"}</span>}
                            <span>{row.categoryName}</span>
                        </div>
                    );
                },
            },
            {
                title: "revenue_account",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetCategoryMapping) => renderAccountCell(row, "revenue"),
            },
            {
                title: "cogs_account",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetCategoryMapping) => renderAccountCell(row, "cogs"),
            },
            {
                title: "inventory_account",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetCategoryMapping) => renderAccountCell(row, "inventory"),
            },
            {
                title: "expense_type",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetCategoryMapping) => {
                    const isInherited = !row.hasDirectMapping && row.inheritedFrom != null;
                    const mapping = row.mapping || row.effectiveMapping;
                    if (!mapping?.expense_type_key) {
                        return <span className={"text-muted"}>—</span>;
                    }
                    return (
                        <div
                            className={`UnanetGlAccountMapping__account-cell ${isInherited ? "UnanetGlAccountMapping__account-inherited" : ""}`}>
                            {mapping.expense_type_code && (
                                <span className={"UnanetGlAccountMapping__account-code"}>{mapping.expense_type_code}</span>
                            )}
                            {mapping.expense_type_name && (
                                <span className={"UnanetGlAccountMapping__account-desc"}>{mapping.expense_type_name}</span>
                            )}
                        </div>
                    );
                },
            },
            {
                title: "status",
                valueProp: "",
                className: "text-center",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetCategoryMapping) => {
                    if (row.hasDirectMapping) {
                        return <OwBadge title={"complete"} bgColor={"bg-success"} />;
                    }
                    if (row.inheritedFrom) {
                        return (
                            <div>
                                <OwBadge title={"inherited"} bgColor={"bg-info"} />
                                <div className={"UnanetGlAccountMapping__inherited-label"}>from {row.inheritedFrom.categoryName}</div>
                            </div>
                        );
                    }
                    return <OwBadge title={"unconfigured"} bgColor={"bg-danger"} />;
                },
            },
            {
                title: "",
                valueProp: "",
                isActionsCol: true,
                className: "text-end",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetCategoryMapping) => {
                    const isConfigured = row.hasDirectMapping || row.inheritedFrom;
                    return (
                        <OwButton
                            title={isConfigured ? "edit" : "configure"}
                            className={`btn btn-sm ${isConfigured ? "btn-outline-secondary" : "btn-outline-primary"}`}
                            fn={() => openEditSidebar(row)}
                            leftIconClass={isConfigured ? "bi bi-pencil" : "bi bi-gear"}
                        />
                    );
                },
            },
        ],
        [openEditSidebar, renderAccountCell]
    );

    return (
        <div className={"UnanetGlAccountMapping"}>
            <Breadcrumbs navItems={navItems} />

            <OwTitle title={"gl_account_mapping"} />

            <div className={"UnanetGlAccountMapping__subtitle"}>
                <OwLabel title={"configure_unanet_accounts"} noBottomMargin />
            </div>

            {summary && (
                <div className={"UnanetGlAccountMapping__summary"}>
                    <span className={"UnanetGlAccountMapping__summary-badge UnanetGlAccountMapping__summary-badge--complete"}>
                        &#10003; Fully Configured: {summary.directlyMapped}
                    </span>
                    <span className={"UnanetGlAccountMapping__summary-badge UnanetGlAccountMapping__summary-badge--inherited"}>
                        &#9888; Partially Configured: {summary.inherited}
                    </span>
                    <span className={"UnanetGlAccountMapping__summary-badge UnanetGlAccountMapping__summary-badge--unconfigured"}>
                        &#10007; Unconfigured: {summary.unmapped}
                    </span>
                </div>
            )}

            <div className={"UnanetGlAccountMapping__filters mb-3"}>
                <OwButton
                    title={"show_all"}
                    className={`btn btn-sm ${filterMode === "all" ? "btn-primary" : "btn-outline-secondary"}`}
                    fn={() => setFilterMode("all")}
                />
                <OwButton
                    title={"fully_configured"}
                    className={`btn btn-sm ${filterMode === "configured" ? "btn-success" : "btn-outline-secondary"}`}
                    fn={() => setFilterMode("configured")}
                />
                <OwButton
                    title={"inherited"}
                    className={`btn btn-sm ${filterMode === "inherited" ? "btn-warning" : "btn-outline-secondary"}`}
                    fn={() => setFilterMode("inherited")}
                />
                <OwButton
                    title={"unconfigured"}
                    className={`btn btn-sm ${filterMode === "unconfigured" ? "btn-danger" : "btn-outline-secondary"}`}
                    fn={() => setFilterMode("unconfigured")}
                />
            </div>

            <OwFnTable
                columns={columns}
                dataSet={filteredCategories}
                dataFetch={isLoading}
                initialData={isInitialLoad}
                tableConfig={{
                    tableHover: true,
                    tableStriped: true,
                }}
            />

            {summary && summary.unmapped > 0 && (
                <div className={"UnanetGlAccountMapping__warning-footer"}>
                    <span className={"UnanetGlAccountMapping__warning-footer-icon"}>&#9888;</span>
                    <span className={"UnanetGlAccountMapping__warning-footer-text"}>
                        <strong>Warning: </strong>
                        Orders containing items from unmapped categories cannot sync to Unanet.
                        <span> ({summary.unmapped} categories need configuration)</span>
                    </span>
                </div>
            )}
        </div>
    );
};
